import { FillFlip } from "@pdfking/filling"
import {
  ChoiceField,
  CreateFormArray,
  CreateFormObject,
  DistanceField,
  DistanceRecordField,
  MultipleChoiceField,
  NumberField,
  StringField,
} from "@pdfking/forms"
import type { Distance } from "@pdfking/math"
import { MARGINS_KEYS, SIZE_KEYS, ZERO_DISTANCE } from "@pdfking/math/constants"
import { MM, QuantityToString } from "@pdfking/quantity"
import { DISTANCE } from "@pdfking/quantity/constants"
import { Constant, Range, RecordCreate, Repeat } from "@pdfking/tfunc"

import { DEFAULT_FILE_BLEED, DEFAULT_SIZE, SECURITY_AREA_DEFAULT } from "#constants"
import { GrowGroups, GrowList } from "#internal"
import type { FormatEntity, JobProcessor, MaterialEntry } from "#internal"

import { FillingList } from "../data/FillingList"
import { FlipList } from "../data/FlipList"
import { HooksMarkingList } from "../data/HooksMarkingList"
import { JobDestinationList, PRINT_DESTINATION_ID } from "../data/JobDestinationList"
import { BuildMaterialChoices } from "../data/MaterialList"
import { OnTopList } from "../data/OnTopList"
import { OrientationKindList } from "../data/OrientationKindList"
import { PrintModeList } from "../data/PrintModeList"

const DistanceDefault = (value: Distance | Record<string, Distance>, unit: string) =>
  Constant(QuantityToString(value, DISTANCE, { unit }))

type Format = FormatEntity
type Suggestions = Record<string, string | undefined>

// Array bounds mirror the python schema (concrete.py:733/738/750). A
// component given no jobs prints nothing, a blank sheet in the book; one
// given none at all prints, its one job the default.
const JOB_BOUNDS = { min: 0, max: 4, initial: Constant([undefined]) }
const COMPONENT_BOUNDS = { min: 1, max: 10 }
// One component per uploaded file, so it stops where the file rows do.
const MARKING_BOUNDS = { min: 0, max: 20 }

// Common uniform padding amounts, offered as suggestions on every
// margins/padding field (a single value broadcasts to every key).
const PADDING_SUGGESTIONS: Suggestions = RecordCreate(
  [0, 1, 2, 3, 5, 10].map(mm => QuantityToString(MM(mm), DISTANCE, { unit: "mm" })),
  Repeat(undefined, 6)
)

const NUMBER_SUGGESTION: Suggestions = RecordCreate(Range(1, 10), Repeat(undefined, 10))

/**
 * The imposition form's field map. Kept separate from the validated form so
 * {@link ImpositionSchema} can be derived from it without depending on the
 * cross-field validators (which are themselves typed against `ImpositionSchema`).
 */
export const imposition_fields = (
  format: Format,
  material_list?: MaterialEntry[],
  size_suggestions?: Suggestions,
  machine_size_suggestions?: Suggestions,
  hooks_list: Record<string, JobProcessor> = HooksMarkingList
) => {
  const JobSchemaForm = CreateFormObject({
    print_mode: ChoiceField(PrintModeList, { format, initial: Constant("fr") }),
    // Required, and never asked for: `ValidateImpositionSizes` gives a job
    // left blank its machine size.
    machine_size: DistanceRecordField({
      keys: SIZE_KEYS,
      min: ZERO_DISTANCE,
      suggestions: machine_size_suggestions,
    }),
    job_destination: ChoiceField(JobDestinationList, {
      format,
      initial: Constant(PRINT_DESTINATION_ID),
    }),
    margins: DistanceRecordField({
      keys: MARGINS_KEYS,
      initial: DistanceDefault(ZERO_DISTANCE, "mm"),
      min: ZERO_DISTANCE,
      input_unit: "mm",
      suggestions: PADDING_SUGGESTIONS,
    }),
  })

  const ComponentSchemaForm = CreateFormObject({
    label: StringField({ optional: true }),
    // grouped by the list each grow comes from: books, boxes, folders…
    grow_kind: ChoiceField<(typeof GrowList)[keyof typeof GrowList]>(
      GrowGroups.map(([id, grows]) => [format({ id }), grows]),
      { format, initial: Constant("b") }
    ),
    material: ChoiceField(BuildMaterialChoices(material_list), {
      format,
      initial: Constant("no"),
    }),
    /**
     * The same page printed this many times in each product, one after the
     * other: a notepad's sheets.
     * @default 1
     */
    copies: NumberField({
      initial: Constant(1),
      min: 1,
      suggestions: NUMBER_SUGGESTION,
    }),
    /** @default "compact" */
    filling: ChoiceField(FillingList, {
      format,
      initial: Constant("compact"),
    }),
    repetitions: NumberField({
      initial: Constant(1),
      suggestions: NUMBER_SUGGESTION,
    }),
    subjects: NumberField({
      initial: Constant(1),
      suggestions: NUMBER_SUGGESTION,
    }),
    padding: DistanceRecordField({
      keys: MARGINS_KEYS,
      initial: DistanceDefault(ZERO_DISTANCE, "mm"),
      input_unit: "mm",
      suggestions: PADDING_SUGGESTIONS,
    }),
    /** @default "1 mm" */
    security_margins: DistanceRecordField({
      keys: MARGINS_KEYS,
      initial: DistanceDefault(SECURITY_AREA_DEFAULT, "mm"),
      min: ZERO_DISTANCE,
      input_unit: "mm",
      suggestions: PADDING_SUGGESTIONS,
    }),
    sheet_depth: DistanceField({ optional: true, min: ZERO_DISTANCE }),
    job_array: CreateFormArray(JobSchemaForm, JOB_BOUNDS),
  })

  return {
    // The job's name: hooks print it (the offset plate name) and the
    // deliverables are named after it.
    filename: StringField({ initial: Constant("file") }),
    orientation_kind: ChoiceField(OrientationKindList, {
      format,
      initial: Constant("n"),
    }),
    size: DistanceRecordField({
      keys: SIZE_KEYS,
      initial: DistanceDefault(DEFAULT_SIZE, "cm"),
      min: ZERO_DISTANCE,
      suggestions: size_suggestions,
    }),
    size_depth: DistanceField({
      initial: DistanceDefault(ZERO_DISTANCE, "cm"),
      input_unit: "mm",
      min: ZERO_DISTANCE,
      suggestions: PADDING_SUGGESTIONS,
    }),
    quantity: NumberField({ initial: Constant(1), min: 0 }),
    component_array: CreateFormArray(ComponentSchemaForm, COMPONENT_BOUNDS),
    marking: MultipleChoiceField(hooks_list, {
      format,
      ...MARKING_BOUNDS,
    }),
    flip: ChoiceField(FlipList, { format, initial: Constant(FillFlip.FLIP_EFFICIENT) }),
    /**
     * How far the media shows past each page: the page's own share of the
     * sheet, capped here.
     * @default "5 mm"
     */
    bleed: DistanceRecordField({
      keys: MARGINS_KEYS,
      initial: DistanceDefault(DEFAULT_FILE_BLEED, "mm"),
      min: ZERO_DISTANCE,
      input_unit: "mm",
      suggestions: PADDING_SUGGESTIONS,
    }),
    on_top: ChoiceField(OnTopList, { format, initial: Constant("on") }),
  }
}
